import { useEffect, useRef, useState } from "react";

import { Scrollbars } from "react-custom-scrollbars-2";

import { EndpointType } from "../constants/Endpoints";
import { DEFAULT_VIEW_TYPE, ViewType } from "../constants/EndpointViews";
import { ChangedFileProvider } from "../hooks/useChangedFile";
import DetailFooter from "../molecules/detail-viewer/DetailFooter";
import DetailHeader from "../molecules/detail-viewer/DetailHeader";
import ResultDownloadButton from "../molecules/result-file/ResultDownloadButton";
import { ResultView } from "../molecules/result-file/ResultView";
import { ResultViewSelect } from "../molecules/result-file/ResultViewSelect";
import { ImageOacJson } from "../types/demo";
import { ImageExtractorOacJson } from "../types/extractorOacResponse";
import { Group, Line } from "../types/extractorResult";
import { ImageLAOacJson } from "../types/laOacResponse";
import { ImageOCROacJson } from "../types/ocrOacResponse";
import { endpointStore } from "../utils/mobx/EndpointStore";
import { imageViewStore } from "../utils/mobx/ImageViewStore";
import { resultInteractionStore } from "../utils/mobx/ResultInteractionStore";

interface ValidatorDetailViewProps {
  file: ImageOacJson;
}

export function ValidatorDetailView({
  file,
}: ValidatorDetailViewProps): JSX.Element {
  const { endpointType } = endpointStore;
  const { reset, setReset } = imageViewStore;
  const { setSelectedBoxIds, setFocusIndex } = resultInteractionStore;

  const [dragging, setDragging] = useState(false);
  const [lineGroups, setLineGroups] = useState<Line[][]>([]);
  const [sortedGroups, setGroups] = useState<Group[]>([]);
  const [isEmpty, setIsEmpty] = useState(false);
  const [view, setView] = useState<ViewType>(DEFAULT_VIEW_TYPE);

  const scrollbarsRef = useRef<Scrollbars>(null);

  useEffect(() => {
    if (endpointType === "extractor") {
      const extractorResult = file as ImageExtractorOacJson;
      if (extractorResult?.fields?.length > 0) {
        setIsEmpty(true);
      }
    } else if (endpointType === "ocr") {
      const ocrResult = file as ImageOCROacJson;
      if (ocrResult?.text?.length > 0) {
        setIsEmpty(true);
      }
    } else if (endpointType === "layout-analysis") {
      const laResult = file as ImageLAOacJson;
      if (laResult?.elements?.length > 0) {
        setIsEmpty(true);
      }
    }
  }, [file]);

  useEffect(() => {
    const onkeydown = function (e: KeyboardEvent) {
      if (e.code === "Space") {
        setDragging(true);
      }
      if (e.code === "Escape") {
        setSelectedBoxIds([]);
        setFocusIndex(-1);
      }
    };

    const onkeyup = function (e: KeyboardEvent) {
      if (e.code === "Space") {
        setDragging(false);
      }
    };

    window.addEventListener("keydown", onkeydown);
    window.addEventListener("keyup", onkeyup);

    return () => {
      window.removeEventListener("keydown", onkeydown);
      window.removeEventListener("keyup", onkeyup);
    };
  }, []);

  return (
    <div className={"flex flex-1 flex-col items-center overflow-y-hidden"}>
      {/* 결과창 헤더 */}
      <ChangedFileProvider
        value={{
          groupChangedResult: sortedGroups,
          lineChangedResult: lineGroups,
          setGroupChangedResult: setGroups,
          setLineChangedResult: setLineGroups,
        }}
      >
        <div className="w-full">
          <DetailHeader />
        </div>

        {/* 결과창 표부분 */}
        {file && isEmpty ? (
          <section className="mb-4 flex h-full w-full flex-1 flex-col gap-1.5 px-4 mobile:mt-1.5">
            <div className="flex w-full items-center justify-between">
              <ResultViewSelect
                view={view}
                setView={setView}
                endpointType={endpointType as EndpointType}
                onViewChange={() => {
                  setReset(!reset);
                  scrollbarsRef.current?.scrollTop(0);
                }}
              />
              <ResultDownloadButton viewType={view} />
            </div>

            <Scrollbars ref={scrollbarsRef}>
              <ResultView view={view} dragging={dragging} />
            </Scrollbars>
          </section>
        ) : (
          <section className="flex h-full w-full flex-1 items-center justify-center gap-2 text-muted-dark">
            <p>No result</p>
          </section>
        )}

        {/* 결과창 푸터 */}
        {isEmpty && (
          <div className="w-full">
            <DetailFooter />
          </div>
        )}
      </ChangedFileProvider>
    </div>
  );
}
